import { ClerkProvider } from "@clerk/clerk-react";
import { ReforgeProvider } from "@reforge-com/react";
import { MotionConfig } from "framer-motion";
import * as React from "react";
import * as Analytics from "~/contexts/analyticsContext";
import * as Authentication from "~/contexts/authenticationContext";
import * as Dialog from "~/contexts/dialogContext";
import * as Drawer from "~/contexts/drawer";
import { MobileMenuProvider } from "~/contexts/mobileMenuContext";
import * as Portal from "~/contexts/portalContext";
import * as ReactRouterStubs from "~/contexts/reactRouterStubsContext";
import * as RequestHeaders from "~/contexts/requestHeadersContext";
import * as Search from "~/contexts/searchContext";
import * as TrackedExperiments from "~/contexts/trackedExperiments";
import * as FeatureFlags from "~/domain/featureFlags";
import { clerkOptions } from "~/lib/clerk";

export type Props = {
  children?: React.ReactNode;
  clerkPublishableKey?: string | null;
  reforgeApiKey?: string;
  analytics: Analytics.AnalyticsContextProps;
  authentication: Authentication.AuthenticationContextProps;
  search: Search.SearchContextProps;
  dialog: Dialog.DialogContextProps;
  drawer: Drawer.DrawerContextProps;
  portal: Portal.PortalContextProps;
  requestHeaders: RequestHeaders.RequestHeadersContextProps;
  reactRouterStubs: ReactRouterStubs.ReactRouterStubs;
};

export const MarketplaceContexts: React.FC<Props> = (props) => {
  const [trackedExperimentsState, trackedExperimentsDispatch] =
    React.useReducer(TrackedExperiments.reducer, TrackedExperiments.init());

  const prefabContext = React.useMemo(
    () =>
      FeatureFlags.makeContext({
        anonymousId: props.analytics.anonymousId ?? undefined,
        identity: props.authentication.identity ?? undefined,
      }),
    [props.analytics.anonymousId, props.authentication.identity],
  );

  const commonProviders = (
    <TrackedExperiments.DispatchContext.Provider
      value={trackedExperimentsDispatch}
    >
      <TrackedExperiments.StateContext.Provider value={trackedExperimentsState}>
        <Analytics.AnalyticsContext.Provider value={props.analytics}>
          <Authentication.AuthenticationContext.Provider
            value={props.authentication}
          >
            <ReforgeProvider
              sdkKey={props.reforgeApiKey ?? "not-set"}
              contextAttributes={prefabContext}
              collectEvaluationSummaries={false}
            >
              <MotionConfig reducedMotion="user">
                <Search.SearchContext.Provider value={props.search}>
                  <Dialog.DialogContext.Provider value={props.dialog}>
                    <Portal.PortalContext.Provider value={props.portal}>
                      <RequestHeaders.RequestHeadersContext.Provider
                        value={props.requestHeaders}
                      >
                        <ReactRouterStubs.ReactRouterStubsContext.Provider
                          value={props.reactRouterStubs}
                        >
                          <MobileMenuProvider>
                            <Drawer.DrawerContext.Provider value={props.drawer}>
                              {props.children}
                            </Drawer.DrawerContext.Provider>
                          </MobileMenuProvider>
                        </ReactRouterStubs.ReactRouterStubsContext.Provider>
                      </RequestHeaders.RequestHeadersContext.Provider>
                    </Portal.PortalContext.Provider>
                  </Dialog.DialogContext.Provider>
                </Search.SearchContext.Provider>
              </MotionConfig>
            </ReforgeProvider>
          </Authentication.AuthenticationContext.Provider>
        </Analytics.AnalyticsContext.Provider>
      </TrackedExperiments.StateContext.Provider>
    </TrackedExperiments.DispatchContext.Provider>
  );

  if (props.clerkPublishableKey) {
    return (
      <ClerkProvider
        publishableKey={props.clerkPublishableKey ?? "not-set"}
        allowedRedirectOrigins={clerkOptions.allowedRedirectOrigins}
        appearance={clerkOptions.appearance}
        localization={clerkOptions.localization}
      >
        {commonProviders}
      </ClerkProvider>
    );
  } else {
    return commonProviders;
  }
};
